import { useEffect, useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { useEngagement } from '@features/engagement/model/use-engagement';
import {
  applyFilters,
  formatCount,
  formatMoney,
  NO_SUBSCRIPTION,
  type EngagementTier,
} from '@features/engagement/model/engagement.types';
import { EngagementTable } from '@features/engagement/ui/EngagementTable';
import { PayVsUseChart } from '@features/engagement/ui/PayVsUseChart';
import { TierDonut } from '@features/engagement/ui/TierDonut';
import { UsageFunnel, type FunnelStage } from '@features/engagement/ui/UsageFunnel';
import { useSubscriptionStatuses } from '@features/users/model/use-users';
import { humanizeStatus } from '@features/users/model/user.types';
import { ChartCard } from '@features/analytics/ui/ChartCard';
import { MultiSelect, type MultiSelectOption } from '@shared/ui/MultiSelect';
import { Spinner } from '@shared/ui/Spinner';
import { useDebouncedValue } from '@shared/hooks/use-debounced-value';
import type { ApiError } from '@shared/api/api-error';

/**
 * Who is actually using the product.
 *
 * The page is built around one question — how many people got past creating a
 * budget — so the funnel and the ladder come first, and the per-user table is
 * the evidence underneath rather than the headline.
 */
export function EngagementPage() {
  // Filters live in the URL, so a view worth sharing can be linked.
  const [searchParams, setSearchParams] = useSearchParams();

  const urlSearch = searchParams.get('q') ?? '';
  const tierParam = searchParams.get('tier') ?? '';
  const statusParam = searchParams.get('status') ?? '';

  const tiers = useMemo(
    () => tierParam.split(',').map((entry) => entry.trim()).filter(Boolean) as EngagementTier[],
    [tierParam],
  );

  const statuses = useMemo(
    () => statusParam.split(',').map((entry) => entry.trim()).filter(Boolean),
    [statusParam],
  );

  const [searchInput, setSearchInput] = useState(urlSearch);
  const debouncedSearch = useDebouncedValue(searchInput.trim(), 350);

  // Keep the box in step when the URL changes from outside (back button, a link).
  useEffect(() => {
    setSearchInput(urlSearch);
  }, [urlSearch]);

  const updateParams = (next: Record<string, string>) => {
    setSearchParams(
      (current) => {
        const merged = new URLSearchParams(current);

        for (const [key, value] of Object.entries(next)) {
          if (value) {
            merged.set(key, value);
          } else {
            merged.delete(key);
          }
        }

        return merged;
      },
      { replace: true },
    );
  };

  useEffect(() => {
    if (debouncedSearch !== urlSearch) {
      updateParams({ q: debouncedSearch });
    }
    // `urlSearch` is intentionally excluded: reacting to it here would fight the
    // sync above and loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  const { data, isPending, isFetching, isError, error } = useEngagement();
  const availableStatuses = useSubscriptionStatuses();

  // Typing filters as fast as it renders: the rows are already here, and the
  // report behind them is too expensive to fetch again per keystroke.
  const visible = useMemo(
    () => applyFilters(data?.users ?? [], { search: debouncedSearch, tiers, statuses }),
    [data?.users, debouncedSearch, tiers, statuses],
  );

  const statusOptions: MultiSelectOption[] = [
    ...(availableStatuses.data ?? []).map((value) => ({
      value,
      label: humanizeStatus(value),
    })),
    { value: NO_SUBSCRIPTION, label: 'No subscription' },
  ];

  const overview = data?.overview;
  // Currencies are ranked by how many budgets sit in each, so the head of the
  // list is the platform's main one rather than whichever has the biggest number.
  const [largestCurrency] = overview?.valueByCurrency ?? [];

  const stages: FunnelStage[] = overview
    ? [
        {
          label: 'Signed up',
          value: overview.users,
          hint: 'every account created',
        },
        {
          label: 'Created a budget',
          value: overview.activated,
          hint: 'got as far as starting one',
        },
        {
          label: 'Put real work in',
          value: overview.engaged,
          hint: 'added real budget lines',
        },
        {
          label: 'Came back recently',
          value: overview.activeRecently,
          hint: 'worked on a budget in the last 30 days',
        },
      ]
    : [];

  const isFiltered = Boolean(urlSearch || tiers.length || statuses.length);

  return (
    // This page scrolls as a whole: the funnel and the ladder are the point, and
    // pinning them above a scrolling table would leave the table a slit.
    <div className="min-h-0 flex-1 overflow-y-auto">
      <div className="space-y-3 pb-3">
        {isPending ? (
          <div className="flex items-center gap-2 py-16 text-sm text-slate-500">
            <Spinner className="h-4 w-4" />
            Building the usage report…
          </div>
        ) : isError ? (
          <p className="py-16 text-center text-sm text-red-600">{(error as ApiError).message}</p>
        ) : (
          overview && (
            <>
              {/* One row, three reads on the same base: how far people get,
                  where they end up, and whether the payers are the users. */}
              <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
                <ChartCard title="Signup to real use" description="How many get to real use">
                  <UsageFunnel stages={stages} />
                </ChartCard>

                <ChartCard
                  title="Where users end up"
                  description="Click a rung to filter the list below"
                >
                  <TierDonut
                    overview={overview}
                    selected={tiers}
                    onChange={(next) => updateParams({ tier: next.join(',') })}
                  />
                </ChartCard>

                <ChartCard
                  title="Who pays, and who uses it"
                  description={`${formatCount(overview.lostAfterRealWork)} more built budgets, then left`}
                  action={
                    largestCurrency && (
                      <p
                        className="shrink-0 text-right"
                        title={overview.valueByCurrency
                          .map(
                            (entry) =>
                              `${formatMoney(entry.amount, entry.currency)} across ${formatCount(entry.budgets)} budgets`,
                          )
                          .join('\n')}
                      >
                        <span className="block text-sm font-semibold tracking-tight text-slate-900">
                          {formatMoney(largestCurrency.amount, largestCurrency.currency)}
                        </span>
                        <span className="block text-[10px] uppercase tracking-wide text-slate-400">
                          budgeted
                        </span>
                      </p>
                    )
                  }
                >
                  <PayVsUseChart users={data?.users ?? []} />
                </ChartCard>
              </div>
            </>
          )
        )}

        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search
              aria-hidden
              className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
            />
            <input
              type="search"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              placeholder="Search users"
              aria-label="Search users by email, name or organization"
              className="h-9 w-64 rounded-lg border border-slate-200 bg-white pl-8 pr-3 text-sm text-slate-900 placeholder:text-slate-400 focus:border-brand-500"
            />
          </div>

          <MultiSelect
            className="w-44"
            placeholder="All statuses"
            values={statuses}
            options={statusOptions}
            onChange={(next) => updateParams({ status: next.join(',') })}
          />

          {isFiltered && (
            <button
              type="button"
              onClick={() => updateParams({ q: '', tier: '', status: '' })}
              className="text-xs text-slate-500 underline-offset-2 hover:text-slate-900 hover:underline"
            >
              Clear filters
            </button>
          )}

          <span className="flex w-4 justify-center">
            {isFetching && !isPending && <Spinner className="h-4 w-4" />}
          </span>

          {data && (
            <p className="ml-auto text-xs tabular-nums text-slate-500">
              {formatCount(visible.length)} of {formatCount(data.overview.users)} users
            </p>
          )}
        </div>

        <EngagementTable
          users={visible}
          loading={isPending}
          error={isError ? (error as ApiError).message : null}
        />
      </div>
    </div>
  );
}
